import { supabase } from "../../lib/supabase/client.js";
import { supabaseCustomRole } from "../../lib/supabase/customRoleClient.js";

/**
 * Send an email using Supabase Edge Function with retry mechanism
 * @param {Object} emailData The email data to send
 * @returns {Promise<Object>} Promise with the result of the operation
 */
export async function sendEmail(emailData) {
  try {
    console.log('Invio email con i seguenti dati:', {
      to: emailData.to,
      subject: emailData.subject,
      replyTo: emailData.replyTo || 'noreply@gildy.it'
    });
    
    // Configurazione dei tentativi
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 secondi tra i tentativi
    
    // Funzione per ritardare l'esecuzione
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Funzione per tentare l'invio dell'email con retry
    const attemptSendEmail = async (retryCount = 0) => {
      try {
        // Imposta un timeout più lungo per la chiamata all'Edge Function (45 secondi)
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout durante l\'invio dell\'email')), 45000);
        });
        
        // Chiama l'Edge Function con un timeout
        const emailPromise = supabase.functions.invoke('smtp-endpoint', {
          body: JSON.stringify(emailData),
        });
        
        // Usa Promise.race per gestire il timeout
        const result = await Promise.race([emailPromise, timeoutPromise]);
        
        // Estrai i dati e gli errori dal risultato
        const { data, error } = result;
        
        if (error) {
          throw error;
        }
        
        console.log('Email inviata con successo tramite Edge Function a:', emailData.to);
        return { success: true };
      } catch (error) {
        // Se abbiamo ancora tentativi disponibili, riprova
        if (retryCount < MAX_RETRIES - 1) {
          console.log(`Tentativo ${retryCount + 1} fallito. Riprovo tra ${RETRY_DELAY/1000} secondi...`);
          await delay(RETRY_DELAY);
          return attemptSendEmail(retryCount + 1);
        }
        
        // Se abbiamo esaurito i tentativi, registra l'errore e restituisci un fallback
        console.error(`Tutti i ${MAX_RETRIES} tentativi di invio email falliti:`, error);
        
        // Registra i dettagli dell'email per un possibile invio manuale successivo
        console.log('FALLBACK: Registrazione dell\'email per invio manuale:', {
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.body ? emailData.body.substring(0, 100) + '...' : '[Contenuto vuoto]'
        });
        
        // Restituisci successo per non bloccare il flusso dell'applicazione
        // ma includi un flag che indica che è stato utilizzato il fallback
        return { 
          success: true, 
          usedFallback: true,
          originalError: error instanceof Error ? 
                        error.message : 
                        'Errore durante l\'invio dell\'email dopo multipli tentativi'
        };
      }
    };
    
    // Avvia il processo di invio con retry
    return await attemptSendEmail();
  } catch (error) {
    console.error('Eccezione durante l\'invio dell\'email:', error);
    
    // Per qualsiasi errore non gestito, restituisci comunque successo con fallback
    return { 
      success: true,
      usedFallback: true,
      originalError: error instanceof Error ? error.message : 'Errore sconosciuto durante l\'invio dell\'email' 
    };
  }
}

/**
 * Send a notification email for a new contact form submission
 * @param {Object} contactData The contact form data
 * @returns {Promise<Object>} Promise with the result of the operation
 */
export async function sendContactNotificationEmail(contactData) {
  try {
    // Admin email address - use the one provided or fall back to the default
    const adminEmail = contactData.adminEmail || process.env.SMTP_TO_EMAIL || 'cpalmeri@molecole.com';
    
    // Format the email subject
    const subject = `Nuovo messaggio di contatto da ${contactData.name}`;
    
    // Format the email body with HTML
    const body = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            h1 { color: #007bff; }
            .info { margin-bottom: 20px; }
            .label { font-weight: bold; }
            .message { background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin-top: 20px; }
            .footer { margin-top: 30px; font-size: 12px; color: #6c757d; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Nuovo messaggio di contatto</h1>
            <p>È stato ricevuto un nuovo messaggio di contatto dal sito web.</p>
            
            <div class="info">
              <p><span class="label">Nome:</span> ${contactData.name}</p>
              <p><span class="label">Email:</span> ${contactData.email}</p>
              <p><span class="label">Telefono:</span> ${contactData.phone || 'Non fornito'}</p>
              <p><span class="label">Data:</span> ${new Date(contactData.created_at).toLocaleString('it-IT')}</p>
              <p><span class="label">ID Messaggio:</span> ${contactData.id}</p>
            </div>
            
            <div class="message">
              <p><span class="label">Messaggio:</span></p>
              <p>${contactData.message.replace(/\n/g, '<br>')}</p>
            </div>
            
            <div class="footer">
              <p>Questo è un messaggio automatico. Non rispondere a questa email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Configura le opzioni per l'invio dell'email
    const emailOptions = {
      to: adminEmail,
      subject,
      body,
      replyTo: contactData.email,
      isHtml: true
    };
    
    // Invia l'email con un timeout più lungo
    console.log(`Invio email a ${adminEmail} con oggetto "${subject}"`);
    
    // Invia l'email
    const result = await sendEmail(emailOptions);
    
    if (!result.success) {
      console.error(`Errore nell'invio dell'email: ${result.error}`);
    } else {
      console.log('Email inviata con successo');
    }
    
    return result;
  } catch (error) {
    console.error('Errore durante l\'invio dell\'email di notifica:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto durante l\'invio dell\'email' 
    };
  }
}

/**
 * Update the email_sent status for a contact message
 * @param {string} messageId The ID of the message to update
 * @param {boolean} emailSent Whether the email was sent successfully
 * @returns {Promise<Object>} Promise with the result of the operation
 */
export async function updateContactEmailStatus(messageId, emailSent) {
  try {
    const { error } = await supabaseCustomRole
      .from('contact_messages')
      .update({ email_sent: emailSent })
      .eq('id', messageId);
    
    if (error) {
      console.error('Error updating contact message email status:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Exception updating contact message email status:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error updating email status' 
    };
  }
}

/**
 * Send a notification email for a dealer activation
 * @param {Object} activationData The dealer activation data
 * @returns {Promise<Object>} Promise with the result of the operation
 */
export async function sendDealerActivationEmail(activationData) {
  // Admin email address - this should be configured in environment variables in production
  const adminEmail = 'admin@gildy.com';
  
  // Format the email subject
  const subject = `Attivazione Dealer Completata: ${activationData.dealerName}`;
  
  // Format the email body with HTML
  const body = `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          h1 { color: #007bff; }
          .info { margin-bottom: 20px; }
          .label { font-weight: bold; }
          .message { background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin-top: 20px; }
          .footer { margin-top: 30px; font-size: 12px; color: #6c757d; }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>Attivazione Dealer Completata</h1>
          <p>L'attivazione del dealer è stata completata con successo.</p>
          
          <div class="info">
            <p><span class="label">Nome Dealer:</span> ${activationData.dealerName}</p>
            <p><span class="label">Email:</span> ${activationData.dealerEmail}</p>
            <p><span class="label">ID Dealer:</span> ${activationData.dealerId}</p>
            <p><span class="label">Data Attivazione:</span> ${new Date(activationData.activatedAt).toLocaleString('it-IT')}</p>
          </div>
          
          <div class="message">
            <p>Il dealer è ora attivo e può accedere a tutte le funzionalità della piattaforma.</p>
          </div>
          
          <div class="footer">
            <p>Questo è un messaggio automatico. Non rispondere a questa email.</p>
          </div>
        </div>
      </body>
    </html>
  `;
  
  // Send the email to admin
  const adminResult = await sendEmail({
    to: adminEmail,
    subject,
    body,
    isHtml: true
  });
  
  // If dealer has an email, send notification to them as well
  if (activationData.dealerEmail) {
    const dealerSubject = 'La tua attivazione su Gildy è stata completata';
    const dealerBody = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            h1 { color: #007bff; }
            .info { margin-bottom: 20px; }
            .message { background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin-top: 20px; }
            .footer { margin-top: 30px; font-size: 12px; color: #6c757d; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Attivazione Completata</h1>
            <p>Gentile ${activationData.dealerName},</p>
            <p>Siamo lieti di informarti che la tua attivazione su Gildy è stata completata con successo.</p>
            
            <div class="message">
              <p>Ora puoi accedere a tutte le funzionalità della piattaforma.</p>
              <p>Per qualsiasi domanda o assistenza, non esitare a contattarci.</p>
            </div>
            
            <div class="footer">
              <p>Grazie per aver scelto Gildy.</p>
              <p>Questo è un messaggio automatico. Non rispondere a questa email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    await sendEmail({
      to: activationData.dealerEmail,
      subject: dealerSubject,
      body: dealerBody,
      isHtml: true
    });
  }
  
  return adminResult;
}

/**
 * Send a notification email for a dealer report
 * @param {Object} reportData The dealer report data
 * @returns {Promise<Object>} Promise with the result of the operation
 */
export async function sendDealerReportNotificationEmail(reportData) {
  try {
    // Admin email address - use the one provided or fall back to the default
    const adminEmail = process.env.SMTP_TO_EMAIL || 'cpalmeri@molecole.com';
    
    // Format the email subject
    const subject = `Nuova segnalazione dealer: ${reportData.dealerName}`;
    
    // Map report types to Italian descriptions
    const reportTypeMap = {
      'CLOSED_PERMANENTLY': 'Chiuso definitivamente',
      'NOT_A_DEALER': 'Non è un compro oro',
      'WRONG_ADDRESS': 'Indirizzo errato',
      'OTHER': 'Altro'
    };
    
    const reportTypeDescription = reportTypeMap[reportData.reportType] || reportData.reportType;
    
    // Format the email body with HTML
    const body = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            h1 { color: #dc3545; }
            .info { margin-bottom: 20px; }
            .label { font-weight: bold; }
            .dealer-info { background-color: #f8f9fa; padding: 15px; border-left: 4px solid #dc3545; margin: 20px 0; }
            .report-info { background-color: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin: 20px 0; }
            .user-info { background-color: #d1ecf1; padding: 15px; border-left: 4px solid #17a2b8; margin: 20px 0; }
            .footer { margin-top: 30px; font-size: 12px; color: #6c757d; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Nuova segnalazione dealer</h1>
            <p>È stata ricevuta una nuova segnalazione per un dealer.</p>
            
            <div class="dealer-info">
              <h3>Informazioni Dealer</h3>
              <p><span class="label">Nome:</span> ${reportData.dealerName}</p>
              <p><span class="label">Indirizzo:</span> ${reportData.dealerAddress}</p>
              <p><span class="label">Città:</span> ${reportData.dealerCity}</p>
            </div>
            
            <div class="report-info">
              <h3>Dettagli Segnalazione</h3>
              <p><span class="label">Tipo:</span> ${reportTypeDescription}</p>
              <p><span class="label">ID Segnalazione:</span> ${reportData.id}</p>
              <p><span class="label">Data:</span> ${new Date(reportData.created_at).toLocaleString('it-IT')}</p>
              ${reportData.description ? `<p><span class="label">Descrizione:</span></p><p>${reportData.description.replace(/\n/g, '<br>')}</p>` : ''}
            </div>
            
            <div class="user-info">
              <h3>Informazioni Utente</h3>
              <p><span class="label">Email:</span> ${reportData.userEmail || 'Non disponibile'}</p>
              <p><span class="label">Nome:</span> ${reportData.userName || 'Non disponibile'}</p>
            </div>
            
            <div class="footer">
              <p>Accedi al pannello di amministrazione per gestire questa segnalazione.</p>
              <p>Questo è un messaggio automatico. Non rispondere a questa email.</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Configura le opzioni per l'invio dell'email
    const emailOptions = {
      to: adminEmail,
      subject,
      body,
      replyTo: reportData.userEmail || 'noreply@gildy.it',
      isHtml: true
    };
    
    // Invia l'email
    console.log(`Invio email segnalazione dealer a ${adminEmail} con oggetto "${subject}"`);
    
    const result = await sendEmail(emailOptions);
    
    if (!result.success) {
      console.error(`Errore nell'invio dell'email segnalazione: ${result.error}`);
    } else {
      console.log('Email segnalazione dealer inviata con successo');
    }
    
    return result;
  } catch (error) {
    console.error('Errore durante l\'invio dell\'email di segnalazione dealer:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto durante l\'invio dell\'email' 
    };
  }
}
